import { useEffect, useEffectEvent } from "react";
import type { ReadonlyJSONValue } from "@rocicorp/zero";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { queries } from "../../../shared/queries";
import type { WorkflowPageView } from "../../../shared/workflowPageView";
import { useQuery } from "@rocicorp/zero/react";
import { useToolOutput } from "../../hooks/useToolOutput";
import { driveLiveStep } from "../../lib/webpageDebugBridge";
import {
  isWebpageDebugTool,
  type WebpageDebugArgs,
  WebpageDebugPrompt,
  type WebpageDebugToolName,
} from "../../tools/WebpageDebug";
import { useProvideChildToolResult } from "./SubAgentPrompt";

// Module-level so switching chat tabs doesn’t reopen a preview the user closed.
const openedPages = new Set<string>();

type PageRoute = { route: string; stepDir: string; externalId: string | null };

const runStepName = (entry: ChatEntryWithSubTables) =>
  (entry.toolCall?.input as { step?: string } | null)?.step;

export function SubAgentPage({
  chatId,
  onShowPage,
}: {
  chatId: string;
  onShowPage: (view: WorkflowPageView) => void;
}) {
  const [agents] = useQuery(queries.subAgentsForChat({ chatId }));
  const agent = agents.findLast(
    (agent) =>
      agent.branchId &&
      (agent.status === "running" || agent.status === "needs_input")
  );
  if (!agent?.branchId) return null;
  return (
    <AgentPage
      key={agent.id}
      agentId={agent.id}
      workflowId={agent.workflowId}
      branchId={agent.branchId}
      turnStartedAt={agent.boundRespondingAt ?? 0}
      onShowPage={onShowPage}
    />
  );
}

function AgentPage({
  agentId,
  workflowId,
  branchId,
  turnStartedAt,
  onShowPage,
}: {
  agentId: string;
  workflowId: string;
  branchId: string;
  turnStartedAt: number;
  onShowPage: (view: WorkflowPageView) => void;
}) {
  const [workflow] = useQuery(
    queries.workflowBranchRoutes({ workflowId, branchId })
  );
  const space = workflow?.space;
  const routes = space
    ? (workflow.branches?.headCommit?.routes ?? []).filter(
        (route) => route.routeType === "webpage"
      )
    : [];
  const showRoute = (route: PageRoute) => {
    if (!workflow || !space) return;
    onShowPage({
      workflowId,
      workflowName: workflow.name,
      spaceSlug: space.slug,
      branchId,
      route: route.route,
      externalId: route.externalId,
    });
  };
  const [commits] = useQuery(queries.loadBranchChats({ branchId }));
  const chats = commits.flatMap((commit) =>
    commit.chats ? [commit.chats] : []
  );
  const calls = chats
    .flatMap((chat) =>
      (chat.entries as ChatEntryWithSubTables[]).filter(
        (entry) => entry.toolCall
      )
    )
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  const pageRun = calls.findLast(
    (entry) =>
      entry.toolCall?.toolName === "runStep" &&
      entry.toolCall.completedAt != null &&
      !entry.toolCall.isError &&
      routes.some((route) => route.stepDir === runStepName(entry))
  );
  const pageRunRoute =
    pageRun && routes.find((route) => route.stepDir === runStepName(pageRun));
  const drive: WebpageDebugArgs["drive"] = (stepName, command) => {
    const route = routes.find((route) => route.stepDir === stepName);
    if (!route)
      return Promise.resolve({
        error: `Step “${stepName}” doesn’t serve a webpage on this branch, so there’s no page to inspect.`,
      });
    return driveLiveStep(stepName, command, () => showRoute(route));
  };
  const pending = calls.findLast(
    (entry) =>
      entry.createdAt >= turnStartedAt &&
      entry.toolCall?.completedAt == null &&
      isWebpageDebugTool(entry.toolCall?.toolName ?? "")
  );
  const pendingCall = pending?.toolCall;
  // The child releases its turn once it waits on the call, as the builder’s chat expects before resolving it.
  const waiting =
    chats.find((chat) => chat.id === pending?.chatId)?.respondingAt == null;
  return (
    <>
      {pageRun && pageRunRoute && (
        <OpenWhenRendered
          key={pageRun.id}
          pageKey={`${agentId}:${pageRunRoute.route}`}
          entryId={pageRun.id}
          onOpen={() => showRoute(pageRunRoute)}
        />
      )}
      {pending &&
        pendingCall &&
        isWebpageDebugTool(pendingCall.toolName) &&
        waiting && (
          <BrowserCall
            key={pending.id}
            chatId={pending.chatId}
            entryId={pending.id}
            branchId={branchId}
            toolName={pendingCall.toolName}
            input={pendingCall.input}
            drive={drive}
          />
        )}
    </>
  );
}

// Opens the preview only once the page renders, rather than on the template the agent starts from.
function OpenWhenRendered({
  pageKey,
  entryId,
  onOpen,
}: {
  pageKey: string;
  entryId: string;
  onOpen: () => void;
}) {
  const { output } = useToolOutput(entryId, true);
  const rendered = (output as { status?: string } | null)?.status === "success";
  const open = useEffectEvent(onOpen);
  useEffect(() => {
    if (!rendered || openedPages.has(pageKey)) return;
    openedPages.add(pageKey);
    open();
  }, [pageKey, rendered]);
  return null;
}

function BrowserCall({
  chatId,
  entryId,
  branchId,
  toolName,
  input,
  drive,
}: {
  chatId: string;
  entryId: string;
  branchId: string;
  toolName: WebpageDebugToolName;
  input: ReadonlyJSONValue;
  drive: WebpageDebugArgs["drive"];
}) {
  const onResult = useProvideChildToolResult({ chatId, entryId, branchId });
  return (
    <WebpageDebugPrompt
      toolCallId={entryId}
      toolName={toolName}
      input={input}
      args={{ drive }}
      onResult={onResult}
    />
  );
}
